import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Unified API request function
export async function apiRequest(
  url: string,
  options?: { method?: string; body?: any }
): Promise<any> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Stringify body if it's an object
  const body = options?.body 
    ? typeof options.body === 'string' 
      ? options.body 
      : JSON.stringify(options.body)
    : undefined;

  const res = await fetch(url, {
    method: options?.method || "GET",
    headers,
    body,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  
  // Handle empty responses (like 204 No Content)
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return null;
  }
  
  // Check if response has content to parse
  const contentType = res.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return null;
  }
  
  return await res.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = {};

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(queryKey.join("/") as string, {
      headers,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000, // 10 minutes - increased for better performance
      gcTime: 15 * 60 * 1000, // 15 minutes - keep data in memory longer
      retry: (failureCount, error) => {
        // Don't retry on 401/403 errors
        if (error.message.includes('401') || error.message.includes('403')) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry on validation errors (400) or auth errors (401/403)
        if (error.message.includes('400') || error.message.includes('401') || error.message.includes('403')) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

